import React, { useEffect, useState } from "react";
import { alpha } from "@mui/material/styles";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import AddIcon from "@mui/icons-material/Add";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { NotIncludedSection } from "/imports/ui/components/booking";
import { wall } from "../upfrontSessionsHelpers";

function SessionsStickyBar({
  currency,
  effectivePrice,
  regularPrice = 0,
  memberNote = null,
  countLabel,
  canContinue,
  selectedSessions,
  showLocation,
  existingTicked,
  onRemove,
  onClear,
  notIncluded = [],
  onAddClubClass,
  onCancelClubClass,
  minPicks,
  sessionsN,
  mode,
  onContinue
}) {
  const [listOpen, setListOpen] = useState(false);
  const selectedCount = selectedSessions.length;
  const progress = sessionsN > 0 ? Math.min(100, (selectedCount / sessionsN) * 100) : 0;

  // Clubs run the same class at several venues and the cards look alike, so a
  // client can tick one branch's date and another's without noticing. Allowed —
  // the pack covers both — but said out loud before they pay.
  const pickedLocations = [...new Set(selectedSessions.map((c) => c.location?.name).filter(Boolean))];
  const locationList =
    pickedLocations.length > 1 ? `${pickedLocations.slice(0, -1).join(", ")} and ${pickedLocations[pickedLocations.length - 1]}` : null;

  // The club's own bookings are ticked on arrival without the client touching
  // anything, and a snackbar saying so is gone in seconds — open the list so
  // they can see which classes this pack is about to pay for.
  useEffect(() => {
    if (existingTicked) setListOpen(true);
  }, [existingTicked]);

  const label = (c) =>
    [wall(c.startDate).toFormat("ccc d LLL"), c.time, c.trainer?.name, showLocation ? c.location?.name : null].filter(Boolean).join(" · ");

  return (
    <Box
      sx={{
        position: "fixed",
        // Clears BottomNav, which is fixed at bottom 0 below md on non-checkout routes.
        bottom: { xs: "3.5rem", md: 0 },
        left: 0,
        right: 0,
        bgcolor: "background.paper",
        borderTop: "1px solid",
        borderColor: "divider",
        boxShadow: "0 -6px 24px rgba(26,26,26,0.08)",
        zIndex: 1000
      }}
    >
      <LinearProgress variant="determinate" value={progress} sx={{ height: "0.25rem" }} />
      <Box sx={{ maxWidth: "60rem", mx: "auto", px: 2, py: 1.75 }}>
        <Collapse in={listOpen}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, pb: 1.5, maxHeight: "35vh", overflowY: "auto" }}>
            {selectedCount === 0 ? (
              <Typography variant="caption" color="text.secondary">
                Nothing picked yet — tap a class to add it.
              </Typography>
            ) : (
              selectedSessions.map((c) =>
                c.joinedUnbilled ? (
                  // Already on the calendar — the club booked it and never charged
                  // for it. ✕ offers to cancel it, so the seat isn't left taken and unpaid.
                  <Chip
                    key={c._id}
                    size="small"
                    color="primary"
                    variant="outlined"
                    icon={<EventAvailableIcon />}
                    label={`${label(c)} · already booked`}
                    onDelete={() => onRemove(c)}
                    sx={{ maxWidth: 1, bgcolor: (t) => alpha(t.palette.primary.main, 0.08) }}
                  />
                ) : (
                  <Chip key={c._id} size="small" label={label(c)} onDelete={() => onRemove(c)} sx={{ maxWidth: 1 }} />
                )
              )
            )}
            {selectedSessions.some((c) => !c.joinedUnbilled) && (
              <Button
                size="small"
                color="error"
                startIcon={<DeleteSweepOutlinedIcon />}
                onClick={onClear}
                sx={{
                  textTransform: "none",
                  height: "1.5rem",
                  px: 1,
                  minWidth: 0,
                  fontSize: "0.8125rem",
                  "& .MuiButton-startIcon": { mr: 0.5, ml: 0, "& svg": { fontSize: "1rem" } }
                }}
              >
                Clear all
              </Button>
            )}
          </Box>

          <NotIncludedSection count={notIncluded.length} totalChips={selectedCount + notIncluded.length} sx={{ pb: 1.5 }}>
            {notIncluded.map((c) => (
              <Chip
                key={`not-included:${c._id}`}
                size="small"
                variant="outlined"
                icon={<AddIcon />}
                label={label(c)}
                onClick={() => onAddClubClass(c)}
                onDelete={() => onCancelClubClass(c)}
                sx={{ maxWidth: 1, color: "text.secondary", borderStyle: "dashed" }}
              />
            ))}
          </NotIncludedSection>
        </Collapse>

        {locationList && (
          <Alert severity="warning" sx={{ mb: 1.25, py: 0, "& .MuiAlert-message": { fontSize: "0.8125rem" } }}>
            Your classes are at {pickedLocations.length} different locations: <strong>{locationList}</strong>. Please check you can make each one.
          </Alert>
        )}

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                {regularPrice > effectivePrice && (
                  <Box component="span" sx={{ mr: 0.75, fontWeight: 400, textDecoration: "line-through" }}>
                    {currency} {regularPrice.toFixed(2)}
                  </Box>
                )}
                {currency} {effectivePrice.toFixed(2)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {countLabel} sessions
              </Typography>
              {/* Where the picks must stay for the member price — it moves as they do. */}
              {memberNote && (
                <Typography variant="caption" sx={{ display: "block", color: memberNote.warning ? "warning.dark" : "primary.main", fontWeight: 600 }}>
                  {memberNote.text}
                </Typography>
              )}
            </Box>
            <IconButton
              size="small"
              onClick={() => setListOpen((o) => !o)}
              aria-label={listOpen ? "Hide picked sessions" : "Show picked sessions"}
              sx={{ ml: { xs: 1.5, sm: 0 }, flexShrink: 0 }}
            >
              {listOpen ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
            </IconButton>
          </Box>
          <Button
            variant="contained"
            disabled={!canContinue}
            onClick={onContinue}
            sx={{ textTransform: "none", color: "common.white", minWidth: "11rem", flexShrink: 0 }}
          >
            {selectedCount < minPicks
              ? mode === "all"
                ? `Pick ${sessionsN - selectedCount} more`
                : "Pick at least 1 session"
              : "Continue to payment"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default SessionsStickyBar;
